"use client";

// Public, DB-backed live scholarships list rendered below the editorial
// country guide. Pulls PUBLISHED entries (both mentor-authored and admin-
// curated) for the given country (case-insensitive). Falls back to a
// short empty state if nothing is published yet — the editorial guide
// remains the primary content above.

import useSWR from "swr";
import {
  LuSparkles,
  LuExternalLink,
  LuCalendar,
  LuCalendarOff,
  LuUserRound,
  LuShield,
  LuCoins,
} from "react-icons/lu";

const API = process.env.NEXT_PUBLIC_API_BASE_URL;

type LiveListing = {
  id: string;
  title: string;
  description: string;
  country: string;
  eligibility: string;
  amount: number | null;
  currency: string | null;
  deadline: string | null;
  applicationUrl: string | null;
  createdAt: string;
  mentorProfile: {
    id: string;
    displayName: string | null;
    avatar: string | null;
  } | null;
  addedByAdmin: { id: string } | null;
};

type ApiResponse = {
  listings: LiveListing[];
  total: number;
  page: number;
  limit: number;
};

const fetcher = (url: string) =>
  fetch(url).then(async (res) => {
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  });

function formatAmount(
  amount: number | null,
  currency: string | null,
): string | null {
  if (amount == null) return null;
  const code = currency?.trim() ? currency.toUpperCase() : "USD";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${code} ${amount.toLocaleString()}`;
  }
}

function formatDeadline(d: string | null): string | null {
  if (!d) return null;
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function isExpired(d: string | null): boolean {
  if (!d) return false;
  const t = new Date(d).getTime();
  return Number.isFinite(t) && t < Date.now();
}

function ListingCard({ listing }: { listing: LiveListing }) {
  const expired = isExpired(listing.deadline);
  const formatted = formatDeadline(listing.deadline);
  const amount = formatAmount(listing.amount, listing.currency);

  return (
    <article
      className={`p-4 lg:p-5 rounded-xl bg-white/60 dark:bg-white/3 border border-gray-200/80 dark:border-white/8 hover:border-indigo-200 dark:hover:border-indigo-500/20 transition-colors ${
        expired ? "opacity-70" : ""
      }`}
    >
      <header className="flex items-start justify-between gap-3 mb-2">
        <h4 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-white leading-snug">
          {listing.title}
        </h4>
        <div className="flex items-center gap-1.5 shrink-0">
          {formatted && (
            <span
              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                expired
                  ? "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300"
                  : "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
              }`}
              title={
                expired ? `Deadline was ${formatted}` : `Deadline ${formatted}`
              }
            >
              {expired ? (
                <LuCalendarOff className="w-3 h-3" />
              ) : (
                <LuCalendar className="w-3 h-3" />
              )}
              {expired ? "Expired" : formatted}
            </span>
          )}
          {listing.applicationUrl && !expired && (
            <a
              href={listing.applicationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-indigo-500 dark:text-indigo-400 transition-colors"
              title="Open application page"
            >
              <LuExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </header>

      <p className="text-sm lg:text-[15px] text-gray-600 dark:text-gray-300 leading-relaxed mb-3 line-clamp-3">
        {listing.description}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {amount && (
          <div className="inline-flex items-center gap-1.5 text-xs lg:text-sm px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-500/15">
            <LuCoins className="w-3.5 h-3.5" />
            {amount}
          </div>
        )}
        {listing.eligibility && (
          <div className="inline-flex items-start gap-1.5 text-xs lg:text-sm px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-200/60 dark:border-blue-500/15 max-w-full">
            <span className="font-medium shrink-0">Eligibility:</span>
            <span className="line-clamp-1">{listing.eligibility}</span>
          </div>
        )}
        <div className="ml-auto inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
          {listing.addedByAdmin ? (
            <>
              <LuShield className="w-3 h-3" />
              Curated
            </>
          ) : listing.mentorProfile ? (
            <>
              <LuUserRound className="w-3 h-3" />
              Mentor: {listing.mentorProfile.displayName ?? "—"}
            </>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function CardSkeleton() {
  return (
    <div className="p-4 lg:p-5 rounded-xl bg-white/60 dark:bg-white/3 border border-gray-200/80 dark:border-white/8 animate-pulse">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="h-4 w-2/3 bg-gray-200 dark:bg-white/10 rounded" />
        <div className="h-4 w-16 bg-gray-200 dark:bg-white/10 rounded-full" />
      </div>
      <div className="space-y-2 mb-3">
        <div className="h-3 bg-gray-200 dark:bg-white/10 rounded" />
        <div className="h-3 bg-gray-200 dark:bg-white/10 rounded w-5/6" />
      </div>
      <div className="flex gap-2">
        <div className="h-6 w-20 bg-gray-200 dark:bg-white/10 rounded-lg" />
        <div className="h-6 w-32 bg-gray-200 dark:bg-white/10 rounded-lg" />
      </div>
    </div>
  );
}

export default function LiveScholarshipsSection({
  countryName,
}: {
  countryName: string;
}) {
  const { data, error, isLoading } = useSWR<ApiResponse>(
    `${API}/scholarships/listings?country=${encodeURIComponent(countryName)}&limit=20`,
    fetcher,
    { revalidateOnFocus: false },
  );

  // Hide the section entirely on hard errors (this is supplementary content;
  // we don't want to break the editorial guide if the API is down).
  if (error) return null;

  const listings = data?.listings ?? [];
  // Hide empty state on the public country page — the editorial sections
  // above are the primary content; an empty "Live" block adds nothing.
  if (!isLoading && listings.length === 0) return null;

  return (
    <section className="mt-10 lg:mt-14">
      <div className="flex items-center gap-3 mb-3 lg:mb-4">
        <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-500/10">
          <LuSparkles className="w-5 h-5 text-indigo-500 dark:text-indigo-400" />
        </div>
        <div>
          <h2 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white">
            Live opportunities
          </h2>
          <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400">
            Curated scholarships and mentor-shared programs, sorted by deadline.
          </p>
        </div>
      </div>
      <div className="space-y-3 lg:space-y-4">
        {isLoading
          ? [1, 2, 3].map((i) => <CardSkeleton key={i} />)
          : listings.map((l) => <ListingCard key={l.id} listing={l} />)}
      </div>
    </section>
  );
}
